import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import BottomMenu from "../components/BottomMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHistory, faBoxes, faComments, faEnvelope, faChevronRight, faSignOutAlt, faShieldAlt } from "@fortawesome/free-solid-svg-icons";
import { getVoucherBalance } from "../services/vouchersHandling";
import { privacyContent } from "../content/privacy";

export default function AccountScreen() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Resolve user/token like PaymentHistory
  const userFromState = location.state?.user;
  const tokenFromState = location.state?.token;
  const user = userFromState ?? auth?.user;
  const token = tokenFromState ?? auth?.token;
  const authReady = tokenFromState !== undefined || auth?.token !== undefined;

  const [voucherBalance, setVoucherBalance] = useState(null);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [selectedFridge] = useState(() => localStorage.getItem("selectedFridgeId") ?? null);

  useEffect(() => {
    if (!authReady) return;
    if (!token) navigate("/");
  }, [authReady, token, navigate]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!authReady || !user?.id || !token) {
        if (!cancelled) setVoucherBalance(null);
        return;
      }
      try {
        const bal = await getVoucherBalance({ userId: user.id, token });
        if (!cancelled) setVoucherBalance(bal);
      } catch {
        if (!cancelled) setVoucherBalance(null);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [authReady, user, token]);

  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  const handleLogout = async () => {
    try {
      await sleep(400);
      try { sessionStorage.clear(); } catch {}
      if ("caches" in window) {
        try {
          const names = await caches.keys();
          await Promise.all(names.map(n => caches.delete(n)));
        } catch {}
      }
      try { await auth.logout?.(); } catch {}
      window.location.replace("/");
    } catch {
      window.location.replace("/");
    }
  };

  const handlePaymentHistoryClick = () => {
    navigate("/paymenthistory", { state: { user, token } });
  };

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    return user?.email?.charAt(0).toUpperCase() || "?";
  };

  if (!user) {
    return (
      <Container>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Laddar profil...</LoadingText>
        </LoadingContainer>
        <BottomMenuSpacer />
        <BottomMenu selectedFridgeId={selectedFridge} />
      </Container>
    );
  }

  const accountMenuItems = [
    { id: "history", title: "Betalningshistorik", subtitle: "Se alla dina transaktioner", icon: faHistory, onClick: handlePaymentHistoryClick, color: "#374151" },
    { id: "units", title: "Mina enheter", subtitle: "Hantera kopplade enheter", icon: faBoxes, onClick: () => navigate("/units"), color: "#374151" },
    { id: "support", title: "Support & hjälp", subtitle: "Kontakta oss för hjälp", icon: faComments, onClick: () => navigate("/support"), color: "#374151" },
    { id: "contact", title: "Kontakt", subtitle: "Hitta våra kontaktuppgifter", icon: faEnvelope, onClick: () => navigate("/contact"), color: "#374151" },
    { id: "privacy", title: "Integritetspolicy", subtitle: "Läs hur vi hanterar dina uppgifter", icon: faShieldAlt, onClick: () => setShowPrivacy(true), color: "#374151" },
  ];

  return (
    <Container>
      <ContentWrapper>
        <ProfileHeader>
          <ProfileBackground>
            <ProfileBackgroundOverlay />
            <ProfileContent>
              <AvatarContainer>
                <Avatar>
                  <AvatarInitials>{getUserInitials()}</AvatarInitials>
                </Avatar>
                <OnlineIndicator />
              </AvatarContainer>
              <UserInfo>
                <UserName>{user.firstName} {user.lastName}</UserName>
                <UserEmail>{user.email}</UserEmail>
                {/* Balance moved to bottom-right badge */}
              </UserInfo>
              {/* Absolutely positioned balance at bottom-right of header */}
              <UserBalance>
                Saldo: {voucherBalance !== null ? `${Number(voucherBalance).toFixed(2)} kr` : "Hämtar..."}
              </UserBalance>
            </ProfileContent>
          </ProfileBackground>
        </ProfileHeader>

        <MenuSection>
          <SectionTitle>Mitt konto</SectionTitle>
          {accountMenuItems.map(item => (
            <MenuCard key={item.id} onClick={item.onClick}>
              <MenuIconContainer $color={item.color}>
                <FontAwesomeIcon icon={item.icon} />
              </MenuIconContainer>
              <MenuContent>
                <MenuTitle>{item.title}</MenuTitle>
                <MenuSubtitle>{item.subtitle}</MenuSubtitle>
              </MenuContent>
              <MenuArrow>
                <FontAwesomeIcon icon={faChevronRight} />
              </MenuArrow>
            </MenuCard>
          ))}
        </MenuSection>

        <LogoutSection>
          <LogoutCard onClick={handleLogout}>
            <LogoutIcon>
              <FontAwesomeIcon icon={faSignOutAlt} />
            </LogoutIcon>
            <LogoutText>Logga ut</LogoutText>
          </LogoutCard>
        </LogoutSection>
      </ContentWrapper>

      <BottomMenuSpacer />
      <BottomMenu selectedFridgeId={selectedFridge} />


        {showPrivacy && (
            <ModalOverlay onClick={() => setShowPrivacy(false)}>
                <ModalContent onClick={(e) => e.stopPropagation()}>
                    <ModalHeader>
                        <h2>Integritetspolicy</h2>
                        <CloseButton onClick={() => setShowPrivacy(false)}>×</CloseButton>
                    </ModalHeader>
                    <ModalBody dangerouslySetInnerHTML={{ __html: privacyContent }} />
                </ModalContent>
            </ModalOverlay>
        )}
    </Container>
  );
}

/* Animations */
const spin = keyframes` from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const pulse = keyframes` 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); }`;

/* Styled Components */
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f6f7fa 0%, #e9ecef 100%);
`;
const ContentWrapper = styled.div`
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
`;
const ProfileHeader = styled.div`
  position: relative;
  margin-bottom: 24px;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0,0,0,0.1);
`;
const ProfileBackground = styled.div`
  height: 180px;
  background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
  position: relative;
`;
const ProfileBackgroundOverlay = styled.div`
  position: absolute; inset: 0; background: linear-gradient(135deg, rgba(55,65,81,0.8) 0%, rgba(31,41,55,0.8) 100%);
`;
const ProfileContent = styled.div`
  position: absolute; inset: 0; padding: 0 24px; display: flex; align-items: center; gap: 16px;
`;
const AvatarContainer = styled.div` position: relative; `;
const Avatar = styled.div`
  width: 80px; height: 80px; border-radius: 50%; background: #fff; border: 4px solid #fff;
  display: flex; align-items: center; justify-content: center; box-shadow: 0 8px 20px rgba(0,0,0,0.15);
`;
const AvatarInitials = styled.div` font-size: 28px; font-weight: 700; color: #374151; `;
const OnlineIndicator = styled.div`
  position: absolute; bottom: 4px; right: 4px; width: 18px; height: 18px; background: #374151; border: 3px solid white; border-radius: 50%; animation: ${pulse} 2s infinite;
`;
const UserInfo = styled.div` flex: 1; `;
const UserName = styled.h1` font-size: 22px; font-weight: 700; color: #fff; margin: 0 0 4px; `;
const UserEmail = styled.p` font-size: 14px; color: rgba(255,255,255,0.9); margin: 0 0 6px; `;
const UserBalance = styled.div`
  position: absolute;
  right: 16px;
  bottom: 16px;
  display: inline-block; padding: 6px 12px; border-radius: 9999px; background: rgba(255,255,255,0.2); color: #fff; font-weight: 700; font-size: 13px; backdrop-filter: blur(6px); border: 1px solid rgba(255,255,255,0.28);
`;

const MenuSection = styled.div` margin: 0 -20px 24px; padding: 0 20px; `;
const SectionTitle = styled.h2` font-size: 18px; font-weight: 600; color: #1f2937; margin: 0 0 12px; `;
const MenuCard = styled.div`
  background: rgba(255,255,255,0.95); border: 1px solid rgba(255,255,255,0.8); border-radius: 16px; padding: 16px; display: flex; align-items: center; gap: 14px; cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.08); margin-bottom: 10px;
  &:last-child{ margin-bottom: 0; }
`;
const MenuIconContainer = styled.div`
  width: 44px; height: 44px; border-radius: 12px; background: ${({$color}) => ($color || "#374151") + "15"}; color: ${({$color}) => $color || "#374151"}; display: flex; align-items: center; justify-content: center; font-size: 18px;
`;
const MenuContent = styled.div` flex: 1; `;
const MenuTitle = styled.div` font-size: 16px; font-weight: 600; color: #1f2937; `;
const MenuSubtitle = styled.div` font-size: 13px; color: #6b7280; `;
const MenuArrow = styled.div` color: #9ca3af; `;

const LogoutSection = styled.div` margin: 24px 0 60px 0; `;
const LogoutCard = styled.div`
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(12px);
  border: 2px solid rgba(239,68,68,0.2);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: all .3s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(239,68,68,0.2); border-color: #ef4444; }
`;
const LogoutIcon = styled.div` font-size: 18px; color: #ef4444; `;
const LogoutText = styled.div` font-size: 16px; font-weight: 600; color: #ef4444; `;

const LoadingContainer = styled.div` display: flex; flex-direction: column; align-items: center; justify-content: center; height: 80vh; gap: 16px; `;
const LoadingSpinner = styled.div` width: 40px; height: 40px; border: 3px solid #e5e7eb; border-top: 3px solid #3b82f6; border-radius: 50%; animation: ${spin} 1s linear infinite; `;
const LoadingText = styled.p` font-size: 16px; color: #6b7280; font-weight: 500; `;
const BottomMenuSpacer = styled.div` height: 80px; `;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0,0,0,0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 24px;
  border-radius: 16px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  h2 {
    font-size: 20px;
    color: #252525;
    margin: 0;
    font-weight: 600;
  }
`;

const ModalBody = styled.div`
  line-height: 1.6;
  font-size: 14px;
  color: #333;

  h2, h3 {
    margin-top: 20px;
    font-size: 16px;
    color: #252525;
    font-weight: 600;
  }

  p { margin: 12px 0; }

  a {
    color: #252525;
    text-decoration: underline;
    cursor: pointer;
  }
  a:hover { color: #000; }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background: #f5f5f5;
    color: #252525;
  }
`;
